<template>
  <div class="pengaturan-absensi-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Pengaturan Absensi</h1>
      <p class="text-gray-600">Kelola jam kerja, toleransi keterlambatan, dan aturan absensi</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Jam Kerja Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Jam Kerja</h2>
        
        <el-form 
          :model="attendanceSettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Jam Masuk">
            <div class="flex items-center gap-3">
              <el-time-picker
                v-model="attendanceSettings.checkInTime"
                placeholder="Pilih jam masuk"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600">WIB</span>
            </div>
            <div class="form-hint">Jam mulai kerja untuk karyawan</div>
          </el-form-item>

          <el-form-item label="Jam Pulang">
            <div class="flex items-center gap-3">
              <el-time-picker
                v-model="attendanceSettings.checkOutTime"
                placeholder="Pilih jam pulang"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600">WIB</span>
            </div>
            <div class="form-hint">Jam selesai kerja untuk karyawan</div>
          </el-form-item>

          <el-form-item label="Total Jam Kerja">
            <div class="text-lg font-semibold text-blue-600">
              {{ calculateWorkHours() }}
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Toleransi & Keterlambatan Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Toleransi & Keterlambatan</h2>
        
        <el-form 
          :model="attendanceSettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Toleransi Keterlambatan">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="attendanceSettings.lateTolerance" 
                :min="0" 
                :max="60"
                controls-position="right"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">menit</span>
            </div>
            <div class="form-hint">
              Karyawan yang terlambat hingga {{ attendanceSettings.lateTolerance }} menit tidak akan dihitung terlambat
            </div>
          </el-form-item>

          <el-form-item label="Batas Waktu Absen Masuk">
            <div class="flex items-center gap-3">
              <el-time-picker
                v-model="attendanceSettings.lateCheckInLimit"
                placeholder="Pilih batas waktu"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600">WIB</span>
            </div>
            <div class="form-hint">Batas maksimal waktu untuk absen masuk</div>
          </el-form-item>

          <el-form-item label="Batas Waktu Absen Pulang">
            <div class="flex items-center gap-3">
              <el-time-picker
                v-model="attendanceSettings.earlyCheckOutLimit"
                placeholder="Pilih batas waktu"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600">WIB</span>
            </div>
            <div class="form-hint">Absen pulang sebelum waktu ini dianggap pulang cepat</div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Lokasi Absensi Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Lokasi Absensi</h2>
        
        <el-form 
          :model="attendanceSettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Radius Absensi">
            <div class="flex items-center gap-3">
              <el-input-number 
                v-model="attendanceSettings.attendanceRadius" 
                :min="10" 
                :max="5000"
                :step="10"
                controls-position="right"
                :disabled="saving.attendance"
                class="flex-1"
              />
              <span class="text-sm text-gray-600 w-16">meter</span>
            </div>
            <div class="form-hint">
              Jarak maksimal dari lokasi kantor untuk dapat melakukan absensi ({{ (attendanceSettings.attendanceRadius / 1000).toFixed(2) }} km)
            </div>
          </el-form-item>

          <el-form-item label="Aktifkan Validasi Lokasi">
            <el-switch
              v-model="attendanceSettings.locationValidation"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Karyawan harus berada dalam radius yang ditentukan untuk melakukan absensi
            </div>
          </el-form-item>

          <el-form-item label="Koordinat Kantor">
            <div class="space-y-2">
              <el-input 
                v-model="attendanceSettings.officeLatitude"
                placeholder="Latitude"
                :disabled="saving.attendance"
              >
                <template #prepend>Lat</template>
              </el-input>
              <el-input 
                v-model="attendanceSettings.officeLongitude"
                placeholder="Longitude"
                :disabled="saving.attendance"
              >
                <template #prepend>Long</template>
              </el-input>
            </div>
            <div class="form-hint">
              Koordinat lokasi kantor untuk validasi absensi
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Fitur Otomatis Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Fitur Otomatis</h2>
        
        <el-form 
          :model="attendanceSettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Auto Check-Out">
            <el-switch
              v-model="attendanceSettings.autoCheckOut"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Otomatis melakukan check-out pada jam pulang jika karyawan belum melakukan check-out
            </div>
          </el-form-item>

          <el-form-item label="Reminder Check-In">
            <el-switch
              v-model="attendanceSettings.checkInReminder"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Kirim notifikasi reminder untuk check-in pada jam masuk kerja
            </div>
          </el-form-item>

          <el-form-item label="Reminder Check-Out">
            <el-switch
              v-model="attendanceSettings.checkOutReminder"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Kirim notifikasi reminder untuk check-out pada jam pulang kerja
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Hari Kerja Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Hari Kerja</h2>
        
        <div class="space-y-3">
          <div 
            v-for="day in workDays" 
            :key="day.value"
            class="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
          >
            <label :for="`day-${day.value}`" class="flex-1 cursor-pointer">
              <span class="text-gray-800 font-medium">{{ day.label }}</span>
            </label>
            <el-switch
              :id="`day-${day.value}`"
              v-model="attendanceSettings.workingDays[day.value]"
              :disabled="saving.attendance"
            />
          </div>
        </div>

        <div class="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p class="text-sm text-blue-800">
            <span class="font-medium">Total Hari Kerja:</span> 
            {{ countWorkingDays() }} hari per minggu
          </p>
        </div>
      </div>

      <!-- Pengaturan Lainnya Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <h2 class="text-lg font-semibold text-gray-800 mb-4">Pengaturan Lainnya</h2>
        
        <el-form 
          :model="attendanceSettings" 
          label-width="220px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Wajib Foto Saat Absen">
            <el-switch
              v-model="attendanceSettings.requirePhoto"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Karyawan wajib mengupload foto selfie saat melakukan absensi
            </div>
          </el-form-item>

          <el-form-item label="Izinkan WFH">
            <el-switch
              v-model="attendanceSettings.allowWFH"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Izinkan karyawan untuk melakukan Work From Home
            </div>
          </el-form-item>

          <el-form-item label="Validasi IP Address">
            <el-switch
              v-model="attendanceSettings.ipValidation"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Validasi IP address kantor untuk keamanan tambahan
            </div>
          </el-form-item>

          <el-form-item v-if="attendanceSettings.ipValidation" label="Whitelist IP">
            <el-input 
              v-model="attendanceSettings.allowedIPs"
              type="textarea"
              :rows="3"
              placeholder="Masukkan IP address yang diizinkan (pisahkan dengan koma)"
              :disabled="saving.attendance"
            />
            <div class="form-hint">
              Contoh: 192.168.1.1, 10.0.0.1
            </div>
          </el-form-item>
        </el-form>
      </div>

      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-700 font-medium">Simpan Perubahan</p>
            <p class="text-sm text-gray-600">Pastikan semua pengaturan sudah sesuai sebelum menyimpan</p>
          </div>
          
          <div class="flex gap-3">
            <el-button @click="resetSettings" :disabled="saving.attendance">
              Reset
            </el-button>
            <el-button 
              type="primary" 
              @click="saveAttendanceSettings" 
              :loading="saving.attendance"
            >
              Simpan Perubahan
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Last Updated Info -->
    <div v-if="lastUpdated" class="mt-4 text-center text-sm text-gray-500">
      Terakhir diupdate: {{ formatDate(lastUpdated) }}
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Axios from '@/utils/axiosConfig';

export default {
  name: "PengaturanAbsensiView",
  data() {
    return {
      loading: false,
      saving: {
        attendance: false
      },
      // Attendance Settings Data
      attendanceSettings: {
        checkInTime: '08:00',
        checkOutTime: '17:00',
        lateTolerance: 15,
        lateCheckInLimit: '09:00',
        earlyCheckOutLimit: '16:00',
        attendanceRadius: 100,
        locationValidation: true,
        officeLatitude: '',
        officeLongitude: '',
        autoCheckOut: false,
        checkInReminder: true,
        checkOutReminder: true,
        workingDays: {
          monday: true,
          tuesday: true,
          wednesday: true,
          thursday: true,
          friday: true,
          saturday: false,
          sunday: false
        },
        requirePhoto: true,
        allowWFH: true,
        ipValidation: false,
        allowedIPs: ''
      },
      workDays: [
        { label: 'Senin', value: 'monday' },
        { label: 'Selasa', value: 'tuesday' },
        { label: 'Rabu', value: 'wednesday' },
        { label: 'Kamis', value: 'thursday' },
        { label: 'Jumat', value: 'friday' },
        { label: 'Sabtu', value: 'saturday' },
        { label: 'Minggu', value: 'sunday' }
      ],
      originalSettings: {},
      lastUpdated: null
    };
  },
  computed: {
    ...mapState(['host'])
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await Axios.get(`${this.host}/api/settings/attendance`);
        
        if (response.data) {
          const data = response.data.data || response.data;
          this.attendanceSettings = {
            ...this.attendanceSettings,
            ...data
          };
          // Save original settings for reset
          this.originalSettings = JSON.parse(JSON.stringify(this.attendanceSettings));
          this.lastUpdated = data.updatedAt || null;
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        this.$message.warning('Gagal memuat pengaturan. Menggunakan nilai default.');
      } finally {
        this.loading = false;
      }
    },

    async saveAttendanceSettings() {
      // Validation
      if (!this.attendanceSettings.checkInTime || !this.attendanceSettings.checkOutTime) {
        this.$message.error('Jam masuk dan pulang harus diisi');
        return;
      }

      if (this.attendanceSettings.locationValidation) {
        if (!this.attendanceSettings.officeLatitude || !this.attendanceSettings.officeLongitude) {
          this.$message.error('Koordinat kantor harus diisi jika validasi lokasi aktif');
          return;
        }
      }

      if (this.attendanceSettings.ipValidation && !this.attendanceSettings.allowedIPs) {
        this.$message.error('IP address harus diisi jika validasi IP aktif');
        return;
      }

      // Check if at least one working day is selected
      if (this.countWorkingDays() === 0) {
        this.$message.error('Minimal harus ada 1 hari kerja yang dipilih');
        return;
      }

      this.saving.attendance = true;
      try {
        const response = await Axios.post(`${this.host}/api/settings/attendance`, this.attendanceSettings);
        
        if (response.data) {
          this.lastUpdated = new Date();
          this.originalSettings = JSON.parse(JSON.stringify(this.attendanceSettings));
          this.$message.success('Pengaturan absensi berhasil disimpan!');
        }
      } catch (error) {
        console.error('Error saving attendance settings:', error);
        this.$message.error('Gagal menyimpan pengaturan absensi');
      } finally {
        this.saving.attendance = false;
      }
    },

    resetSettings() {
      this.$confirm('Apakah Anda yakin ingin mereset semua perubahan?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Reset',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        this.attendanceSettings = JSON.parse(JSON.stringify(this.originalSettings));
        this.$message.info('Pengaturan direset ke nilai sebelumnya');
      }).catch(() => {
        // User cancelled
      });
    },

    calculateWorkHours() {
      if (!this.attendanceSettings.checkInTime || !this.attendanceSettings.checkOutTime) {
        return '0 jam 0 menit';
      }

      const [inHour, inMin] = this.attendanceSettings.checkInTime.split(':').map(Number);
      const [outHour, outMin] = this.attendanceSettings.checkOutTime.split(':').map(Number);

      const inMinutes = inHour * 60 + inMin;
      const outMinutes = outHour * 60 + outMin;
      
      let diffMinutes = outMinutes - inMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Handle next day
      }

      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;

      return `${hours} jam ${minutes} menit`;
    },

    countWorkingDays() {
      return Object.values(this.attendanceSettings.workingDays).filter(day => day).length;
    },

    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    }
  }
};
</script>

<style scoped>
.pengaturan-absensi-container {
  max-width: 1200px;
  margin: 0 auto;
}

.settings-form {
  margin-top: 0;
}

.form-hint {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

/* Customize Element UI form items */
:deep(.el-form-item) {
  margin-bottom: 24px;
}

:deep(.el-form-item__label) {
  color: #374151;
  font-weight: 500;
}

:deep(.el-input__inner),
:deep(.el-textarea__inner) {
  border-radius: 8px;
}

:deep(.el-time-picker) {
  width: 100%;
}

:deep(.el-input-number) {
  width: 100%;
}

:deep(.el-switch) {
  height: auto;
}

:deep(.el-switch__label) {
  font-size: 14px;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pengaturan-absensi-container {
    padding: 1rem;
  }

  :deep(.el-form-item) {
    flex-direction: column;
  }

  :deep(.el-form-item__label) {
    text-align: left !important;
    width: 100% !important;
    margin-bottom: 8px;
  }

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}
</style>